import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { trackAction } from '@ctct/distui-helpers';
import { Heading, SubHeading, LoadingIndicator, RadioGroup, DatePicker, Input, Alert } from '@ctct/ui-react';

import { getCurrencySymbol } from '../../../utils/pricingUtils';
import { useCancelFlowContext } from '../CancelFlowContext';
import './PauseFeedback.scss';

const CustomInput = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { onClick, value } = props;

  return (
    <Input
      onClick={onClick}
      ref={ref}
      placeholder={t('cancel-flow.steps.pause-feedback.estimation-placeholder')}
      value={value}
      glyph="calendar"
    />
  );
});

CustomInput.propTypes = {
  onClick: PropTypes.function,
  value: PropTypes.string,
};

CustomInput.defaultProps = {
  onClick: () => {},
  value: '',
};

const PauseFeedback = () => {
  const { t } = useTranslation();
  const [cancelFlowContext, cancelFlowReducer] = useCancelFlowContext();
  const { pauseReason, pauseReasons, estimationDate, pauseCost, currencyCode, mustMakeSelection } = cancelFlowContext;

  const today = new Date();
  const tomorrow = new Date();

  tomorrow.setDate(today.getDate() + 1);

  const afterTwoYears = new Date();

  afterTwoYears.setFullYear(today.getFullYear() + 2);

  return (
    <span className="pause-feedback">
      {mustMakeSelection && (
        <span className="make-selection">
          <Alert kind="warning" message={t('cancel-flow.steps.pause-feedback.make-selection')} />
        </span>
      )}
      <Heading
        level={2}
        text={
          pauseCost && currencyCode ? (
            t('cancel-flow.steps.pause-feedback.heading', {
              cost: `${getCurrencySymbol(currencyCode)}${pauseCost}`,
            })
          ) : (
            <Trans i18nKey="cancel-flow.steps.pause-feedback.heading-unloaded">
              <LoadingIndicator kind="basicSmall" />
            </Trans>
          )
        }
      />
      <Heading className="sub-heading-feedback" level={4} text={t('cancel-flow.steps.pause-feedback.sub-heading')} />
      <RadioGroup
        data-qe-id="pause-suspend-reasons-radio-group"
        value={pauseReason}
        style={{ paddingTop: 15, paddingLeft: 15 }}
        onChoose={(evt, data) => {
          const {
            item: { id: selectedReasonId },
          } = data;

          trackAction({ action: `Select pause/suspend radio button ${selectedReasonId}` });
          cancelFlowReducer({
            type: 'setPauseReason',
            value: selectedReasonId,
          });
        }}
        items={pauseReasons.map((reason) => {
          const { id, description } = reason;

          return {
            id,
            value: id,
            text: t(description),
          };
        })}
      />
      <Heading className="estimation-title" level={4} text={t('cancel-flow.steps.pause-feedback.estimation-heading')} />
      <SubHeading
        level={4}
        text={
          <Trans i18nKey="cancel-flow.steps.pause-feedback.estimation-sub-heading">
            <span>(Optional)</span>
          </Trans>
        }
      />
      <DatePicker
        data-qe-id="MyDatePicker"
        onChange={(date) => {
          cancelFlowReducer({
            type: 'setEstimationDate',
            value: date,
          });
        }}
        selectedDate={estimationDate}
        minDate={tomorrow}
        maxDate={afterTwoYears}
        customInput={<CustomInput />}
      />
    </span>
  );
};

export default PauseFeedback;
